import React, { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../authToken';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Theme A MCQ quiz for one or more selected learning statements.
 * Answers are checked server-side; each POST to /quiz-answer records the
 * attempt, awards XP on first correct, and returns the correct option.
 *
 * Props:
 *   scopes, level, scopeTitle, onBack  — as in Flashcards.
 */
export default function Quiz({ scopes, level, scopeTitle, onBack }) {
  const [questions, setQuestions] = useState([]);
  const [idx,       setIdx]       = useState(0);
  const [loading,   setLoading]   = useState(true);

  // Per-question answer state
  const [selected, setSelected] = useState(null);   // chosen option text
  const [result,   setResult]   = useState(null);   // { correct, correct_answer, awarded_xp }
  const [checking, setChecking] = useState(false);

  // Running tally
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  const scopeKey = scopes.slice().sort().join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          scopeKey.split(',').filter(Boolean).map(s =>
            fetch(`${API_URL}/api/theme-a/content/${s}?level=${level}`, { headers: authHeaders() })
              .then(r => r.json())
          )
        );
        if (!cancelled) {
          const seen = new Set();
          const merged = [];
          for (const d of results) {
            for (const q of (d.questions || [])) {
              if (!seen.has(q.id)) { seen.add(q.id); merged.push(q); }
            }
          }
          setQuestions(merged);
          setIdx(0);
          setSelected(null);
          setResult(null);
          setScore({ correct: 0, answered: 0 });
        }
      } catch (_) {
        if (!cancelled) setQuestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKey, level]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = questions[idx];
  const finished = !loading && questions.length > 0 && idx >= questions.length;

  const answer = useCallback(async (option) => {
    if (result || checking || !current) return;
    setSelected(option);
    setChecking(true);
    try {
      const r = await fetch(`${API_URL}/api/theme-a/quiz-answer`, {
        method:  'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify({ question_id: current.id, answer: option }),
      });
      const d = await r.json();
      setResult(d);
      setScore(s => ({
        correct:  s.correct + (d.correct ? 1 : 0),
        answered: s.answered + 1,
      }));
    } catch (_) {
      setSelected(null);
    } finally {
      setChecking(false);
    }
  }, [result, checking, current]);

  const next = useCallback(() => {
    setSelected(null);
    setResult(null);
    setIdx(i => i + 1);
  }, []);

  const restart = useCallback(() => {
    setIdx(0);
    setSelected(null);
    setResult(null);
    setScore({ correct: 0, answered: 0 });
  }, []);

  function optionClass(opt) {
    if (!result) return 'ta-option';
    if (opt === result.correct_answer) return 'ta-option ta-option-correct';
    if (opt === selected)              return 'ta-option ta-option-wrong';
    return 'ta-option';
  }

  return (
    <div className="ta-canvas">
      <div className="ta-header">
        <button className="ta-back" onClick={onBack}>‹ Topics</button>
        <div>
          <h1 className="ta-title">Quiz</h1>
          <p className="ta-subtitle">{scopeTitle} · {level.toUpperCase()}</p>
        </div>
      </div>

      {loading ? (
        <div className="ta-empty">Loading…</div>
      ) : questions.length === 0 ? (
        <div className="ta-empty">No questions for this selection.</div>
      ) : finished ? (
        <div className="ta-study">
          <div className="ta-summary">
            <div className="ta-summary-label">You scored</div>
            <div className="ta-summary-score">
              {score.correct} / {score.answered}
            </div>
            <div className="ta-summary-label">
              {Math.round((score.correct / Math.max(score.answered, 1)) * 100)}% correct
            </div>
            <div className="ta-controls" style={{ marginTop: 22 }}>
              <button className="ta-btn ta-btn-secondary" onClick={restart}>Try again</button>
              <button className="ta-btn ta-btn-primary" onClick={onBack}>Back to topics</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="ta-study">
          <div className="ta-progress">
            Question {idx + 1} / {questions.length} · Score {score.correct}/{score.answered}
          </div>

          <div className="ta-quiz-card">
            <p className="ta-question">{current.question}</p>

            <div className="ta-options">
              {current.options.map((opt, i) => (
                <button
                  key={i}
                  className={optionClass(opt)}
                  onClick={() => answer(opt)}
                  disabled={!!result || checking}
                >
                  {opt}
                  {result && opt === result.correct_answer && <span className="ta-option-mark">✓</span>}
                  {result && opt === selected && opt !== result.correct_answer && <span className="ta-option-mark">✗</span>}
                </button>
              ))}
            </div>

            {result && (
              <div className="ta-feedback">
                <span className={`ta-verdict ${result.correct ? 'ta-verdict-correct' : 'ta-verdict-wrong'}`}>
                  {result.correct ? 'Correct!' : 'Not quite.'}
                  {result.awarded_xp > 0 && <span className="ta-xp" style={{ marginLeft: 8 }}>+{result.awarded_xp} XP</span>}
                </span>
                <button className="ta-btn ta-btn-primary" onClick={next}>
                  {idx === questions.length - 1 ? 'See results' : 'Next question ›'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
